import Decimal from 'decimal.js';
import { buildMetricValueCsv } from './csvExport';
import type { LedgerEvent } from './types';
import { OMNIBUS_WALLET_KEY } from './walletLotContinuity';

const AMT_EPS = new Decimal('1e-10');

/**
 * CDC `internal_transfer` subreasons (`fiSubreason`) that can correspond to a later on-chain receipt.
 * Includes `WITHDRAWAL_STYLE` for `crypto_withdrawal` / `*withdraw*` under balanced policy — previously
 * omitted, which blocked most CDC→wallet continuity in real exports.
 */
const CDC_CROSS_ENV_SUBREASONS = new Set([
  'CRYPTO_TRANSFER',
  'EXCHANGE_TO_APP',
  'APP_TO_EXCHANGE',
  'TRANSFER_CASHBACK',
  /** `crypto_withdrawal`, etc. — cryptoComCsv `conservativeInternalSubreason` when type includes `withdraw`. */
  'WITHDRAWAL_STYLE',
]);

/** Max gap between CDC internal row and matching chain acquisition when tx hash is absent. */
const MAX_GAP_MS_NO_HASH = 72 * 60 * 60 * 1000;

/** Max gap when at least one side lacks a hash but we still allow match (conservative). */
const MAX_GAP_MS_LOOSE = 14 * 24 * 60 * 60 * 1000;

export type CrossEnvironmentContinuityStats = {
  /** CDC internal ↔ chain TRANSFER_IN pairs linked (omnibus → wallet move). */
  cdcOmnibusToChainMatches: number;
  /** Chain acquisition rows removed as duplicates of CDC-origin inventory. */
  chainTransferInSuppressed: number;
  /** Synthetic internal_transfer rows (omnibus → owned wallet). */
  syntheticOmnibusToWalletInserted: number;
  /** Cronos TRANSFER_IN rows that looked like candidates but were not matched. */
  unmatchedChainTransferInCandidates: number;
  /** CDC internal rows that could match but no chain leg consumed them (remaining). */
  unmatchedCdcInternalCandidates: number;
};

function normAsset(a: string): string {
  const u = a.trim().toUpperCase();
  return u === 'WCRO' ? 'CRO' : u;
}

function normHash(h: string | undefined): string {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/^0x/, '');
}

function amountsMatch(a: Decimal, b: Decimal): boolean {
  if (a.sub(b).abs().lte(AMT_EPS)) return true;
  const max = Decimal.max(a.abs(), b.abs());
  if (max.lte(0)) return false;
  return a.sub(b).abs().div(max).lte(new Decimal('1e-6'));
}

function hashCompatible(cdcHash: string, cronRef: string): boolean {
  const c = normHash(cdcHash);
  const r = normHash(cronRef);
  if (!c || !r) return true;
  if (c === r) return true;
  if (c.length >= 8 && r.length >= 8 && (c.includes(r.slice(0, 10)) || r.includes(c.slice(0, 10))))
    return true;
  return false;
}

function timeWindowOk(cdcTs: number, cronTs: number, cdcHash: string, cronRef: string): boolean {
  if (cronTs < cdcTs) return false;
  const gap = cronTs - cdcTs;
  const c = normHash(cdcHash);
  const r = normHash(cronRef);
  if (c && r && c === r) return gap <= MAX_GAP_MS_LOOSE;
  if (!c || !r) return gap <= MAX_GAP_MS_NO_HASH;
  return gap <= MAX_GAP_MS_LOOSE;
}

function isCdcCrossEnvCandidate(ev: LedgerEvent): boolean {
  return (
    ev.source === 'crypto_com' &&
    ev.kind === 'internal_transfer' &&
    ev.fiTaxCategory === 'NON_TAXABLE_INTERNAL' &&
    CDC_CROSS_ENV_SUBREASONS.has(ev.fiSubreason ?? '')
  );
}

function isCronosExternalTransferIn(ev: LedgerEvent, owned: Set<string>): boolean {
  if (ev.source !== 'cronos') return false;
  if (ev.kind !== 'acquisition') return false;
  if (ev.classification !== 'TRANSFER_IN') return false;
  const to = String(ev.toAddress || '')
    .trim()
    .toLowerCase();
  const from = String(ev.fromAddress || '')
    .trim()
    .toLowerCase();
  if (!to || !owned.has(to)) return false;
  if (from && owned.has(from)) return false;
  return true;
}

/**
 * Links CDC non-taxable internal moves (inventory stays with user; basis in omnibus) to a later
 * on-chain TRANSFER_IN of the same asset/amount, by removing the duplicate chain acquisition and
 * inserting a synthetic omnibus → wallet internal move so wallet-scoped FIFO moves lots like a real self-transfer.
 *
 * Strict: asset match, amount match, time ordering, optional tx hash compatibility, no fuzzy same-day buckets.
 */
export function applyCrossEnvironmentContinuity(
  events: LedgerEvent[],
  options: { ownedAddresses: Set<string> }
): { events: LedgerEvent[]; stats: CrossEnvironmentContinuityStats } {
  const owned = options.ownedAddresses;
  if (owned.size === 0) {
    return {
      events,
      stats: {
        cdcOmnibusToChainMatches: 0,
        chainTransferInSuppressed: 0,
        syntheticOmnibusToWalletInserted: 0,
        unmatchedChainTransferInCandidates: 0,
        unmatchedCdcInternalCandidates: 0,
      },
    };
  }

  const stats: CrossEnvironmentContinuityStats = {
    cdcOmnibusToChainMatches: 0,
    chainTransferInSuppressed: 0,
    syntheticOmnibusToWalletInserted: 0,
    unmatchedChainTransferInCandidates: 0,
    unmatchedCdcInternalCandidates: 0,
  };

  const cdcCandidates = events.filter(isCdcCrossEnvCandidate);
  const cronCandidates = events.filter((e) => isCronosExternalTransferIn(e, owned));

  const usedCdc = new Set<LedgerEvent>();
  const removeCron = new Set<LedgerEvent>();
  const synthetic: LedgerEvent[] = [];

  const cronSorted = [...cronCandidates].sort((a, b) => a.ts - b.ts);

  for (const cron of cronSorted) {
    const asset = normAsset(cron.asset);
    let bestMatch: { ev: LedgerEvent; score: number } | null = null;

    for (const cdc of cdcCandidates) {
      if (usedCdc.has(cdc)) continue;
      if (normAsset(cdc.asset) !== asset) continue;
      if (!amountsMatch(cdc.amount, cron.amount)) continue;
      const cdcHash = cdc.csvMeta?.transactionHash ?? '';
      if (
        !timeWindowOk(cdc.ts, cron.ts, cdcHash, cron.ref ?? '') ||
        !hashCompatible(cdcHash, cron.ref ?? '')
      ) {
        continue;
      }
      const score = cron.ts - cdc.ts;
      if (bestMatch == null || score < bestMatch.score) {
        bestMatch = { ev: cdc, score };
      }
    }

    if (bestMatch != null) {
      usedCdc.add(bestMatch.ev);
      removeCron.add(cron);
      const to = String(cron.toAddress).trim().toLowerCase();
      synthetic.push({
        ts: cron.ts,
        source: 'cronos',
        kind: 'internal_transfer',
        asset: cron.asset,
        amount: cron.amount,
        eurValue: null,
        ref: `cross_env_${cron.ref ?? 'noref'}`,
        classification: 'INTERNAL_TRANSFER',
        fiTaxCategory: 'NON_TAXABLE_INTERNAL',
        fiSubreason: 'CROSS_ENV_CDC_OMNIBUS_TO_CHAIN',
        explanation:
          'Matched Crypto.com non-taxable internal row to this on-chain receipt — omnibus inventory continues to owned wallet (no duplicate acquisition).',
        fromAddress: OMNIBUS_WALLET_KEY,
        toAddress: to,
        primaryWalletAddress: to,
        reviewFlags: ['cross_env_continuity', 'omnibus_to_owned_synthetic'],
        basisContinuityRef: bestMatch.ev.ref,
      });
      stats.cdcOmnibusToChainMatches++;
      stats.chainTransferInSuppressed++;
      stats.syntheticOmnibusToWalletInserted++;
    }
  }

  for (const c of cronCandidates) {
    if (!removeCron.has(c)) stats.unmatchedChainTransferInCandidates++;
  }
  for (const c of cdcCandidates) {
    if (!usedCdc.has(c)) stats.unmatchedCdcInternalCandidates++;
  }

  if (removeCron.size === 0) {
    return { events, stats };
  }

  const out = events.filter((e) => !removeCron.has(e));
  out.push(...synthetic);
  out.sort((a, b) => {
    if (a.ts !== b.ts) return a.ts - b.ts;
    return String(a.ref ?? '').localeCompare(String(b.ref ?? ''));
  });

  return { events: out, stats };
}

export function buildCrossEnvironmentDiagnosticsCsv(
  stats: CrossEnvironmentContinuityStats
): string {
  const rows: string[][] = [
    ['cdc_omnibus_to_chain_matches', String(stats.cdcOmnibusToChainMatches)],
    ['chain_transfer_in_suppressed', String(stats.chainTransferInSuppressed)],
    ['synthetic_omnibus_to_wallet_inserted', String(stats.syntheticOmnibusToWalletInserted)],
    ['unmatched_chain_transfer_in_candidates', String(stats.unmatchedChainTransferInCandidates)],
    ['unmatched_cdc_internal_candidates', String(stats.unmatchedCdcInternalCandidates)],
  ];
  return buildMetricValueCsv(rows);
}
